import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import { InputLabel, List, ListItem, ListItemButton, ListItemText, TextField } from '@mui/material';
import { ArrowRight, FilterListOff } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Iconify from 'src/components/iconify';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { enqueueSnackbar } from 'notistack';
import Label from '../../label';
import CustomPopover, { usePopover } from '../../custom-popover';
import Scrollbar from '../../scrollbar';
import { MetricData } from '../../../models/metricData';
import { updateMetricFavorite } from '../../../services/metrics';

type Props = {
  selected: MetricData[];
  onFilters: (name: string, value: any[]) => void;
  metrics: MetricData[];
  size?: number;
  useLabel?: boolean;
  margin?: number;
};

const maxHeight = 390;
export default function FilterMetrics({
  selected,
  onFilters,
  metrics,
  size = 0,
  useLabel = true,
  margin = 2,
}: Props) {
  const { t } = useTranslation();
  const popover = usePopover();
  const theme = useTheme();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [loadingFavorite, setLoadingFavorite] = useState<string | null>(null);
  const [pendingSelected, setPendingSelected] = useState<MetricData[]>(selected);
  const [pendingSelectedGroup, setPendingSelectedGroup] = useState<string | null>(null);

  // Sincroniza pendingSelected solo cuando se abre el popover o se descarta
  useEffect(() => {
    if (popover.open) {
      setPendingSelected(
        selected.map(
          (m) =>
            new MetricData({
              metrics_name: m.metrics_name,
              metrics_values: [...(m.metrics_values || [])],
              options: m.options ? [...m.options] : [],
              data: m.data ? [...m.data] : [],
              description: m.description,
              favorite: m.favorite,
              isDash: m.isDash,
              id: m.id,
            })
        )
      );
      setPendingSelectedGroup(selectedGroup);
    }
  }, [popover.open, selected, selectedGroup]);

  const handleToggleFavorite = useCallback(
    async (row: MetricData) => {
      if (!row.id) return;
      setLoadingFavorite(row.id);
      const updated = await updateMetricFavorite(row.id, !row.favorite);
      if (!updated) {
        enqueueSnackbar(t('error.updating'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('success.updated'), { variant: 'success' });
        row.setFavorite(!row.favorite);
      }
      setLoadingFavorite(null);
    },
    [t]
  );

  const selectedMetrics = useMemo(
    () =>
      selected?.reduce(
        (acc, curr) => {
          acc[curr.metrics_name] = curr.metrics_values;
          return acc;
        },
        {} as Record<string, string[]>
      ),
    [selected]
  );

  const pendingSelectedMetrics = useMemo(
    () =>
      pendingSelected?.reduce(
        (acc, curr) => {
          acc[curr.metrics_name] = curr.metrics_values;
          return acc;
        },
        {} as Record<string, string[]>
      ),
    [pendingSelected]
  );

  const renderLabel = useMemo(
    () =>
      selected?.length
        ? selected
            .map(
              (m) =>
                `${m.metrics_name}: ${m.metrics_values?.slice(0, 2).join(',')} ${
                  m.metrics_values?.length > 2 ? '...' : ''
                }`
            )
            ?.slice(0, 2)
            ?.join(',')
        : t('text.labels.metrics'),
    [selected, t]
  );

  const handleFilterType = useCallback(
    (group: string, metric: string) => {
      const updatedMetrics = { ...selectedMetrics };
      if (!updatedMetrics[group]) {
        updatedMetrics[group] = [metric];
      } else {
        const index = updatedMetrics[group].indexOf(metric);
        if (index === -1) {
          updatedMetrics[group].push(metric);
        } else {
          updatedMetrics[group].splice(index, 1);
        }
      }
      onFilters(
        'metrics',
        Object.entries(updatedMetrics).map(([name, values]) => ({
          metrics_name: name,
          metrics_values: values,
        }))
      );
    },
    [selectedMetrics, onFilters]
  );

  const handlePendingFilterType = useCallback(
    (group: string, metric: string) => {
      const updatedMetrics = { ...pendingSelectedMetrics };
      if (!updatedMetrics[group]) {
        updatedMetrics[group] = [metric];
      } else {
        const index = updatedMetrics[group].indexOf(metric);
        if (index === -1) {
          updatedMetrics[group].push(metric);
        } else {
          updatedMetrics[group].splice(index, 1);

          // If the group is empty, remove it from pendingSelectedMetrics
          if (updatedMetrics[group].length === 0) {
            delete updatedMetrics[group];
          }
        }
      }
      setPendingSelected(
        Object.entries(updatedMetrics).map(
          ([name, values]) => new MetricData({ metrics_name: name, metrics_values: values })
        )
      );
    },
    [pendingSelectedMetrics]
  );

  const handleSelectAll = useCallback(
    (group: string, m: string[]) => {
      const updatedMetrics = { ...selectedMetrics };
      if (updatedMetrics[group]) {
        delete updatedMetrics[group];
      } else {
        updatedMetrics[group] = m.filter((value) =>
          value.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      onFilters(
        'metrics',
        Object.entries(updatedMetrics).map(([name, values]) => ({
          metrics_name: name,
          metrics_values: values,
          complete: values.length === m.length, // mark as complete if all values are selected
        }))
      );
    },
    [selectedMetrics, onFilters, searchText]
  );

  const handlePendingSelectAll = useCallback(
    (group: string, m: string[]) => {
      const updatedMetrics = { ...pendingSelectedMetrics };
      if (updatedMetrics[group]) {
        delete updatedMetrics[group];
      } else {
        updatedMetrics[group] = m.filter((value) =>
          value.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      setPendingSelected(
        Object.entries(updatedMetrics).map(
          ([name, values]) =>
            new MetricData({
              metrics_name: name,
              metrics_values: values,
              complete: values.length === m.length,
            })
        )
      );
    },
    [pendingSelectedMetrics, searchText]
  );

  // Detecta si hay cambios pendientes (comparación profunda)
  const hasPendingChanges = useMemo(() => {
    const normalize = (arr: MetricData[]) =>
      arr
        .map((m) => ({
          metrics_name: m.metrics_name,
          metrics_values: [...(m.metrics_values || [])].sort(),
        }))
        .sort((a, b) => a.metrics_name.localeCompare(b.metrics_name));
    const normSelected = normalize(selected);
    const normPending = normalize(pendingSelected);
    return JSON.stringify(normSelected) !== JSON.stringify(normPending);
  }, [pendingSelected, selected]);

  // handleApply aplica los cambios
  const handleApply = () => {
    onFilters(
      'metrics',
      pendingSelected.map((m) => new MetricData({ ...m }))
    );
    popover.onClose();
  };

  // handleDiscard descarta los cambios y cierra el popover
  const handleDiscard = () => {
    setPendingSelected(
      selected.map(
        (m) =>
          new MetricData({
            metrics_name: m.metrics_name,
            metrics_values: [...(m.metrics_values || [])],
            options: m.options ? [...m.options] : [],
            data: m.data ? [...m.data] : [],
            description: m.description,
            favorite: m.favorite,
            isDash: m.isDash,
            id: m.id,
          })
      )
    );
    setPendingSelectedGroup(selectedGroup);
    popover.onClose();
  };

  const handleReset = () => {
    setPendingSelected([]);
    setPendingSelectedGroup(null);
  };

  const filteredMetrics = useMemo(
    () =>
      metrics.filter(
        (m) =>
          m.metrics_name.toLowerCase().includes(searchText.toLowerCase()) ||
          m.metrics_values.some((value) => value.toLowerCase().includes(searchText.toLowerCase()))
      ),
    [metrics, searchText]
  );

  const metricLabelItems = (m: string) => {
    if (!pendingSelectedGroup) return null;
    return (
      <FormControlLabel
        key={m}
        control={
          <Checkbox
            checked={pendingSelectedMetrics[pendingSelectedGroup]?.includes(m) || false}
            onChange={() => handlePendingFilterType(pendingSelectedGroup, m)}
          />
        }
        label={m}
        sx={{
          py: 1.5,
          textTransform: 'capitalize',
          '& .MuiFormControlLabel-label': {
            textTransform: 'capitalize',
            width: '100%',
          },
          ...(searchText &&
            m.toLowerCase().includes(searchText.toLowerCase()) && {
              fontWeight: 600,
              color: theme.palette.primary.main,
              transition: theme.transitions.create(['background-color'], {
                duration: theme.transitions.duration.shortest,
              }),
            }),
        }}
      />
    );
  };

  const renderList = (metrics_values: string[] | undefined) => {
    if (!metrics_values) return <></>;
    if (!searchText)
      return metrics_values
        .sort((a, b) => a.localeCompare(b))
        .map((metric) => metricLabelItems(metric));

    // if there is a search text, sort the metrics by the search text
    const sortedMetrics = metrics_values
      .filter((value) => value.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
    const otherMetrics = metrics_values
      .filter((value) => !value.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
    return (
      <Stack divider={<Divider variant="middle" />}>
        {sortedMetrics.map((metric) => metricLabelItems(metric))}
        {otherMetrics.map((metric) => metricLabelItems(metric))}
      </Stack>
    );
  };

  // Al cerrar el filtro, descarta los cambios si no se aplicaron
  useEffect(() => {
    if (!popover.open) {
      setPendingSelected(
        selected.map(
          (m) =>
            new MetricData({
              metrics_name: m.metrics_name,
              metrics_values: [...(m.metrics_values || [])],
              options: m.options ? [...m.options] : [],
              data: m.data ? [...m.data] : [],
              description: m.description,
              favorite: m.favorite,
              isDash: m.isDash,
              id: m.id,
            })
        )
      );
      setPendingSelectedGroup(selectedGroup);
    }
  }, [popover.open, selected, selectedGroup]);

  return (
    <Stack m={margin} sx={{ width: size > 0 ? size : 'auto' }}>
      {useLabel && (
        <InputLabel
          shrink
          sx={{
            ml: 1,
            pr: 2,
            color: `${theme.palette.text.primary} !important`,
            fontSize: '1.1rem !important',
          }}
        >
          {t('text.labels.metrics')}
        </InputLabel>
      )}
      <Button
        variant="contained"
        onClick={popover.onOpen}
        disabled={metrics.length === 0}
        startIcon={
          <Iconify
            icon="eva:options-2-outline"
            sx={{
              mr: 0.5,
              color: theme.palette.action.active,
            }}
          />
        }
        endIcon={
          <Iconify
            icon={popover.open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
            sx={{ ml: -0.5, color: theme.palette.action.active }}
          />
        }
        sx={{
          py: 0,
          px: 2,
          backgroundColor: 'background.paper',
          color: 'text.disabled',
          height: 53,
          justifyContent: 'space-between',
          border: `solid 1px ${theme.palette.divider}`,
          '&:hover': {
            border: `solid 1px ${theme.palette.action.active}`,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Typography
          variant="inherit"
          fontSize={14}
          fontWeight={500}
          sx={{
            textTransform: 'capitalize',
            textAlign: 'left',
            width: '100%',
            color: 'text.disabled',
          }}
        >
          {t('text.labels.metrics')}
          {selected?.length > 0 && (
            <Label color="primary" sx={{ ml: 1 }}>
              {selected.length}
            </Label>
          )}
        </Typography>
      </Button>

      <CustomPopover
        open={popover.open}
        onClose={() => {
          // If the suer don't apply the changes, reset pendingSelected
          handleDiscard();
        }}
        sx={{ p: 2.5, maxWidth: 650, maxHeight: 625, width: '100%' }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <TextField
              label={`${t('text.labels.metrics')} ${t('text.labels.search').toLowerCase()}`}
              variant="filled"
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Tooltip title={t('text.buttons.reset')}>
              <IconButton onClick={handleReset} disabled={pendingSelected.length === 0}>
                <Badge color="primary" variant="dot" invisible={pendingSelected.length === 0}>
                  <FilterListOff />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={2} direction="row" alignItems="start">
            <Stack
              spacing={1.5}
              direction="column"
              width={1 / 2.5}
              sx={{ borderRight: 1, borderColor: 'divider' }}
            >
              <Scrollbar sx={{ maxHeight }}>
                <List>
                  {filteredMetrics.map((m) => (
                    <ListItem
                      key={m.metrics_name}
                      secondaryAction={
                        <ArrowRight
                          fontSize="small"
                          onClick={() =>
                            // Open the right side list for the selected group
                            setPendingSelectedGroup(m.metrics_name)
                          }
                          sx={{
                            color: theme.palette.text.disabled,
                          }}
                        />
                      }
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() => setPendingSelectedGroup(m.metrics_name)}
                        dense
                        sx={{
                          backgroundColor:
                            pendingSelectedGroup === m.metrics_name
                              ? theme.palette.action.selected
                              : 'transparent',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Checkbox
                          edge="start"
                          checked={!!pendingSelectedMetrics[m.metrics_name]}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': m.metrics_name }}
                          onClick={() => handlePendingSelectAll(m.metrics_name, m.metrics_values)}
                          sx={{ width: 30 }}
                        />
                        <Tooltip
                          title={
                            m.favorite
                              ? t('text.labels.removeFavorite')
                              : t('text.labels.addFavorite')
                          }
                        >
                          <span>
                            <IconButton
                              color={m.favorite ? 'error' : 'default'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFavorite(m);
                              }}
                              disabled={loadingFavorite === m.id || m.id === ''}
                              size="small"
                              sx={{
                                width: 30,
                                ...(m.id === '' && {
                                  opacity: 0.5,
                                  pointerEvents: 'none',
                                }),
                              }}
                            >
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {m.favorite ? (
                                <FavoriteIcon sx={{ width: 19, height: 19 }} />
                              ) : (
                                <FavoriteBorderIcon sx={{ width: 19, height: 19 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <ListItemText
                          id={m.metrics_name}
                          primary={m.metrics_name}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Scrollbar>
            </Stack>
            <Stack direction="column" justifyContent="flex-start" width={1.5 / 2.5}>
              {pendingSelectedGroup && (
                <Scrollbar sx={{ maxHeight }}>
                  <Stack px={2} divider={<Divider variant="middle" flexItem />}>
                    {renderList(
                      filteredMetrics
                        .find((metric) => metric.metrics_name === pendingSelectedGroup)
                        ?.metrics_values.filter((value) => value.trim() !== '')
                    )}
                  </Stack>
                </Scrollbar>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" color="inherit" onClick={handleDiscard}>
              {t('text.buttons.discard')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!hasPendingChanges}
              onClick={() => {
                onFilters('metrics', pendingSelected);
                popover.onClose();
              }}
            >
              {t('text.buttons.apply')}
            </Button>
          </Stack>
        </Stack>
      </CustomPopover>
    </Stack>
  );
}
